






import { Component, Mixins, Prop, Watch, Provide } from 'vue-property-decorator';
import MixinCharts from '@/plugins/podolib/services/helper/mixins/chart/standard';
import { SeriesOptionsTypeExtended, TChart } from '@/plugins/podolib/services/helper/mixins/chart';
import { TAcquisition_ComputedData_Standard } from '@/plugins/podolib/services/entities/EntityPatients';

@Component
export default class ChartStancePhaseAverageRanges extends Mixins(MixinCharts) implements TChart {
  @Prop({ required: true }) private side!: 'left' | 'right';

  // Chart name for exportation
  @Provide() readonly name = 'ChartStancePhaseAverageRanges';

  @Provide() average = {
    a: {
      min: -17.5,
      max: -12.5,
    },
    b: {
      min: -12.5,
      max: -7.5,
    },
    c: {
      min: -12.5,
      max: -7.5,
    },
    d: {
      min: -7.5,
      max: -2.5,
    },
  };

  // Calculs
  get __digitigrade(): TAcquisition_ComputedData_Standard {
    const rsl = this.values[this.side].aggregates.propulsion_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }
  get __plantigrade(): TAcquisition_ComputedData_Standard {
    const rsl = this.values[this.side].aggregates.flatfoot_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }
  get __taligrade(): TAcquisition_ComputedData_Standard {
    const rsl = this.values[this.side].aggregates.loading_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __total(): number {
    return this.__digitigrade.avg + this.__plantigrade.avg + this.__taligrade.avg;
  }

  get __heel_strike() {
    return this.values[this.side].aggregates.pronation_angle_foot_in.avg;
  }
  get __heel_strike_range() {
    const proSup = this.values[this.side].aggregates.pronation_angle_foot_in;
    return {
      x: 1,
      low: proSup.avg - this.$pdc.helper!.radToDeg(proSup.std),
      high: proSup.avg + this.$pdc.helper!.radToDeg(proSup.std),
    };
  }

  get __flat_foot() {
    return this.values[this.side].aggregates.pronation_angle_FFI.avg;
  }
  get __flat_foot_range() {
    const proSup = this.values[this.side].aggregates.pronation_angle_FFI;
    return {
      x: 1,
      low: proSup.avg - proSup.std,
      high: proSup.avg + proSup.std,
    };
  }

  get __heel_off() {
    return this.values[this.side].aggregates.pronation_angle_FFO.avg;
  }
  get __heel_off_range() {
    const proSup = this.values[this.side].aggregates.pronation_angle_FFO;
    return {
      x: 1,
      low: proSup.avg - this.$pdc.helper!.radToDeg(proSup.std),
      high: proSup.avg + this.$pdc.helper!.radToDeg(proSup.std),
    };
  }

  get __toe_off() {
    return this.values[this.side].aggregates.pronation_angle_foot_out.avg;
  }
  get __toe_off_range() {
    const proSup = this.values[this.side].aggregates.pronation_angle_foot_out;
    return {
      x: 1,
      low: proSup.avg - proSup.std,
      high: proSup.avg + proSup.std,
    };
  }

  // Series
  get _val(): SeriesOptionsTypeExtended[] {
    return this.isAbsolute
      ? [
          {
            name: `${this.$tc('commons.standards.average')} (-)`,
            id: 'average_min',
            yAxis: 0,
            data: [
              [0, this.average.a.min],
              [this.__taligrade.avg, this.average.b.min],
              [this.__taligrade.avg + this.__plantigrade.avg, this.average.c.min],
              [this.__taligrade.avg + this.__plantigrade.avg + this.__digitigrade.avg, this.average.d.min],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.average',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.chart4 as string,
            dashStyle: 'ShortDot',
            marker: {
              enabled: true,
              symbol: 'diamond',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 1,
            type: 'line',
            openRow: true,
            visible: true,
          },
          {
            name: `${this.$tc('commons.standards.average')} (+)`,
            id: 'average_max',
            yAxis: 0,
            data: [
              [0, this.average.a.max],
              [this.__taligrade.avg, this.average.b.max],
              [this.__taligrade.avg + this.__plantigrade.avg, this.average.c.max],
              [this.__taligrade.avg + this.__plantigrade.avg + this.__digitigrade.avg, this.average.d.max],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.average',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.chart4 as string,
            dashStyle: 'ShortDot',
            marker: {
              enabled: true,
              symbol: 'diamond',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 2,
            type: 'line',
            closeRow: true,
            visible: true,
          },
          {
            name: this.$tc(`commons.standards.${this.side}`),
            id: 'main_1',
            yAxis: 0,
            data: [
              [0, this.__heel_strike],
              [this.__taligrade.avg, this.__flat_foot],
              [this.__taligrade.avg + this.__plantigrade.avg, this.__heel_off],
              [this.__total, this.__toe_off],
            ],
            tooltip: {
              pointFormat: '<div style="color: {series.color}">Position: {point.y:.1f}°</div>',
            },
            color: !this.comparison ? '#4EBFD7' : '#AF7427',
            index: 3,
            type: 'line',
          },
          {
            name: `${this.$tc('commons.standards.range')} (-)`,
            id: 'range_min',
            yAxis: 0,
            data: [
              [0, this.__heel_strike_range.low as number],
              [this.__taligrade.avg, this.__flat_foot_range.low as number],
              [this.__taligrade.avg + this.__plantigrade.avg, this.__heel_off_range.low as number],
              [this.__total, this.__toe_off_range.low as number],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.range',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.grey3 as string,
            marker: {
              enabled: false,
              symbol: 'square',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 4,
            type: 'line',
            openRow: true,
          },
          {
            name: `${this.$tc('commons.standards.range')} (+)`,
            id: 'range_max',
            yAxis: 0,
            data: [
              [0, this.__heel_strike_range.high as number],
              [this.__taligrade.avg, this.__flat_foot_range.high as number],
              [this.__taligrade.avg + this.__plantigrade.avg, this.__heel_off_range.high as number],
              [this.__total, this.__toe_off_range.high as number],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.range',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.grey3 as string,
            marker: {
              enabled: false,
              symbol: 'square',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 5,
            type: 'line',
            closeRow: true,
          },
        ]
      : [
          {
            name: `${this.$tc('commons.standards.average')} (-)`,
            id: 'average_min',
            yAxis: 0,
            data: [
              [0, this.average.a.min],
              [this.__taligrade.avg, this.average.b.min],
              [this.__taligrade.avg + this.__plantigrade.avg, this.average.c.min],
              [this.__taligrade.avg + this.__plantigrade.avg + this.__digitigrade.avg, this.average.d.min],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.average',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.chart4 as string,
            dashStyle: 'ShortDot',
            marker: {
              enabled: true,
              symbol: 'diamond',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 1,
            type: 'line',
            openRow: true,
            visible: false,
          },
          {
            name: `${this.$tc('commons.standards.average')} (+)`,
            id: 'average_max',
            yAxis: 0,
            data: [
              [0, this.average.a.max],
              [this.__taligrade.avg, this.average.b.max],
              [this.__taligrade.avg + this.__plantigrade.avg, this.average.c.max],
              [this.__taligrade.avg + this.__plantigrade.avg + this.__digitigrade.avg, this.average.d.max],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.average',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.chart4 as string,
            dashStyle: 'ShortDot',
            marker: {
              enabled: true,
              symbol: 'diamond',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 2,
            type: 'line',
            closeRow: true,
            visible: false,
          },
          {
            name: this.$tc(`commons.standards.${this.side}`),
            id: 'main_1',
            yAxis: 0,
            data: [
              [0, 0],
              [this.__taligrade.avg, this.__flat_foot - this.__heel_strike],
              [this.__taligrade.avg + this.__plantigrade.avg, this.__heel_off - this.__heel_strike],
              [this.__total, this.__toe_off - this.__heel_strike],
            ],
            tooltip: {
              pointFormat: '<div style="color: {series.color}">Position: {point.y:.1f}°</div>',
            },
            color: !this.comparison ? '#4EBFD7' : '#AF7427',
            index: 3,
            type: 'line',
          },
          {
            name: `${this.$tc('commons.standards.range')} (-)`,
            id: 'range_min',
            yAxis: 0,
            data: [
              [0, this.__heel_strike_range.low! - this.__heel_strike],
              [this.__taligrade.avg, this.__flat_foot_range.low! - this.__heel_strike],
              [this.__taligrade.avg + this.__plantigrade.avg, this.__heel_off_range.low! - this.__heel_strike],
              [this.__total, this.__toe_off_range.low! - this.__heel_strike],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.range',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.grey3 as string,
            marker: {
              enabled: false,
              symbol: 'square',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 4,
            type: 'line',
            openRow: true,
          },
          {
            name: `${this.$tc('commons.standards.range')} (+)`,
            id: 'range_max',
            yAxis: 0,
            data: [
              [0, this.__heel_strike_range.high! - this.__heel_strike],
              [this.__taligrade.avg, this.__flat_foot_range.high! - this.__heel_strike],
              [this.__taligrade.avg + this.__plantigrade.avg, this.__heel_off_range.high! - this.__heel_strike],
              [this.__total, this.__toe_off_range.high! - this.__heel_strike],
            ],
            tooltip: {
              pointFormat: `<div style="color: {series.color}">${this.$tc(
                'commons.standards.range',
              )} : {point.y:.1f}° •`,
            },
            color: this.$vuetify.theme.themes.light.grey3 as string,
            marker: {
              enabled: false,
              symbol: 'square',
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            index: 5,
            type: 'line',
            closeRow: true,
          },
        ];
  }

  get series() {
    return [...this._val];
  }

  get isAbsolute(): boolean {
    return this.mode === 'absolute';
  }

  updateValues() {
    let _this = this;
    this.setOptions({
      chart: {
        height: this.height || 450,
        inverted: true,
        spacingTop: 12,
      },
      title: {
        text: !this.hideTitle
          ? !this.comparison
            ? (`${this.$tc('commons.standards.stages_step', 2)} (${this.$tc(
                `commons.standards.${this.side}`,
              ).toLowerCase()})` as string)
            : this.$tc('commons.standards.comparison')
          : undefined,
      },
      xAxis: {
        title: {
          text: this.$tc('commons.units.millisecond', 2) as string,
        },
        reversed: false,
        plotLines: [
          {
            color: '#aeced7',
            value: 0,
            width: 1,
            zIndex: 2,
            label: {
              text: this.$tc('commons.standards.heel-strike') as string,
            },
          },
          {
            color: '#aeced7',
            value: this.__taligrade.avg,
            width: 1,
            zIndex: 2,
            label: {
              text: this.$tc('commons.standards.toe-strike') as string,
            },
          },
          {
            color: '#aeced7',
            value: this.__taligrade.avg + this.__plantigrade.avg,
            width: 1,
            zIndex: 2,
            label: {
              text: this.$tc('commons.standards.heel-off') as string,
            },
          },
          {
            color: '#aeced7',
            value: this.__taligrade.avg + this.__plantigrade.avg + this.__digitigrade.avg,
            width: 1,
            zIndex: 2,
            label: {
              text: this.$tc('commons.standards.toe-off') as string,
            },
          },
        ],
        plotBands: [
          {
            color: 'rgba(222,246,255,0.52)',
            from: this.__taligrade.avg,
            to: this.__taligrade.avg + this.__plantigrade.avg,
            label: {
              text: this.$t('commons.standards.flat-foot') as string,
              align: 'right',
              x: -10,
            },
            zIndex: 1,
          },
          {
            color: 'rgba(248,249,255,0.52)',
            from: this.__taligrade.avg + this.__plantigrade.avg,
            to: this.__taligrade.avg + this.__plantigrade.avg + this.__digitigrade.avg,
            label: {
              text: this.$t('commons.standards.propulsion') as string,
              align: 'right',
              x: -10,
            },
            zIndex: 1,
          },
          {
            color: 'rgba(203,243,255,0.52)',
            from: 0,
            to: this.__taligrade.avg,
            label: {
              text: this.$t('commons.sentences.charts.loading') as string,
              align: 'right',
              x: -10,
            },
            zIndex: 1,
          },
        ],
        tickInterval: 100,
        max: this.__total,
      },
      yAxis: {
        title: {
          text: this.$tc('commons.units.degree', 2) as string,
        },
        labels: {
          format: '{value}°',
        },
        reversed: this.side === 'right',
        min: !this.simple ? -40 : null,
        max: !this.simple ? 40 : null,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        distance: 50,
        formatter() {
          // String buffer
          let s = '';
          // Iterate on the points
          for (const p of this.points!) {
            const options: SeriesOptionsTypeExtended = p.series.options;
            // Populate string buffer
            if (options.openRow) {
              s += `<tr><td>`;
              s += `<span style="color: ${p.color}">${p.series.name}</span> : `;
              s += `<b style="margin-left: 6px">▼ ${p.point.y!.toFixed(2)}</b> °`;
            } else if (options.closeRow) {
              s += `<b style="margin-left: 6px">▲ ${p.point.y!.toFixed(2)}</b> °`;
              s += `</td></tr>`;
            } else {
              s += `<tr><td>`;
              s += `<span style="color: ${p.color}">${p.series.name}</span> : `;
              s += `<b style="margin-left: 6px">${p.point.y!.toFixed(2)}</b> °`;
              s += `</td></tr>`;
            }
          }
          return `<table><tbody><tr><td>${_this.$t('commons.standards.time')} : <b>${this.points![0].point.x!.toFixed(
            2,
          )}</b> ms</td></tr>${s}</tbody></table>`;
        },
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick() {
              return false;
            },
          },
        },
      },
      series: [],
    });
    this.setSeries(this.series);
  }

  @Watch('values')
  onValuesChange() {
    this.updateValues();
  }

  created() {
    let _this = this;
    this.updateValues();
  }
}
